import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PortalDropdownProps {
  /** The trigger element the menu anchors to. */
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Align the menu's edge to the anchor: 'end' (default) or 'start'. RTL-aware. */
  align?: 'start' | 'end';
  /** Preferred side; flips automatically when there isn't room. */
  placement?: 'bottom' | 'top';
  /** Menu width in px (used for horizontal placement + clamping). */
  width?: number;
  /** Render a click-catching backdrop behind the menu (default true). */
  backdrop?: boolean;
  className?: string;
  children: ReactNode;
}

const GAP = 6;
const EDGE = 8;

/**
 * Renders a dropdown menu in a portal on `document.body` with `position: fixed`,
 * anchored to a trigger element. Because it lives outside any scroll/overflow
 * container, it never expands the page's scroll area or gets clipped — which is
 * what plain `position: absolute` menus do inside grids and scrollable modals.
 */
export function PortalDropdown({
  anchorEl,
  open,
  onClose,
  align = 'end',
  placement = 'bottom',
  width = 176,
  backdrop = true,
  className = '',
  children,
}: PortalDropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchorEl) return;

    const reposition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      const height = menuRef.current?.offsetHeight ?? 0;
      const isRtl = getComputedStyle(anchorEl).direction === 'rtl';

      // Horizontal: anchor the chosen edge, then clamp into the viewport.
      let left = align === 'end'
        ? (isRtl ? rect.left : rect.right - width)
        : (isRtl ? rect.right - width : rect.left);
      left = Math.max(EDGE, Math.min(left, vw - width - EDGE));

      // Vertical: prefer the requested side, flip when there isn't room.
      const spaceBelow = vh - rect.bottom;
      const spaceAbove = rect.top;
      const useTop = placement === 'top'
        ? spaceAbove > height + GAP || spaceAbove >= spaceBelow
        : spaceBelow < height + GAP && spaceAbove > spaceBelow;

      const next: CSSProperties = { position: 'fixed', left, width, visibility: 'visible', zIndex: 9999 };
      if (useTop) next.bottom = vh - rect.top + GAP;
      else next.top = rect.bottom + GAP;
      setStyle(next);
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, anchorEl, align, placement, width]);

  if (!open) return null;

  return createPortal(
    <>
      {backdrop && <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={onClose} />}
      <div ref={menuRef} style={style} className={className}>
        {children}
      </div>
    </>,
    document.body,
  );
}
